import {
  AfterViewInit,
  Component,
  ComponentRef,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  Signal,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ModalConfig } from '@shared/interfaces/modal/modal-config';
import { ModalSize } from '@app/core/types/modal-size';
import { LucideAngularModule, X } from 'lucide-angular';
import { TranslatePipe } from '@ngx-translate/core';
import { ModalService } from '@app/core/services/modal.service';
import { ButtonDirective } from '@shared/components/input/button/button.directive';
import { ModalAction } from '@shared/interfaces/modal/modal-action';
import { injectComponent } from '@app/core/utils/inject-component';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';
import { ConfirmDialogService } from '@app/core/services/confirm-dialog.service';
import { AlertService } from '@app/core/services/alert.service';
import { Alert } from '@shared/interfaces/alert/alert';
import { AlertServiceWrapperComponent } from '@shared/components/alert-service-wrapper/alert-service-wrapper.component';
import { CdkTrapFocus } from '@angular/cdk/a11y';

/**
 * ModalComponent is a dynamic container for rendering modals.
 * It supports various modal types and sizes, handles Escape key closing,
 * and supports alert display and dynamic component injection.
 */
@Component({
  selector: 'app-modal',
  imports: [
    LucideAngularModule,
    TranslatePipe,
    ButtonDirective,
    AlertServiceWrapperComponent,
    CdkTrapFocus,
  ],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.css'
})
export class ModalComponent implements OnInit, AfterViewInit {
  /**
   * The ViewContainerRef where the modal content component is dynamically injected.
   */
  @ViewChild('modalContainer', { read: ViewContainerRef, static: false }) public modalContainer!: ViewContainerRef;

  /**
   * Reference to the modal wrapper element.
   */
  @ViewChild('modalWrapper') public modalWrapper!: ElementRef;

  /**
   * Closes the modal when Escape is pressed.
   * @param event - KeyboardEvent
   */
  @HostListener('document:keydown.esc', ['$event'])
  public onKeyDown(event: Event) {
    if (event instanceof KeyboardEvent) {
      if (event.key === 'Escape' && this.open) {
        this.cancel();
      }
    }
  }

  /**
   * Triggers the submit action when Ctrl+Enter is pressed while the modal is open.
   * Prefers an action marked with `isSubmit`, falls back to the first primary action.
   */
  @HostListener('document:keydown.control.enter', ['$event'])
  public onCtrlEnter(event: Event): void {
    if (!this.open || !this.modalConfig.actions?.length) return;
    event.preventDefault();

    const explicit = this.modalConfig.actions.find(a => a.isSubmit);
    if (explicit) { this.handleAction(explicit); return; }

    const primaries = this.modalConfig.actions.filter(a => !a.type || a.type === 'primary');
    if (primaries.length === 1) this.handleAction(primaries[0]);
  }

  /** Tracks whether the modal is open */
  public open: boolean = false;

  /** Icon reference for close (X) */
  protected readonly X = X;

  /** The configuration object for the modal */
  public modalConfig!: ModalConfig;

  /** Reference to the dynamically injected component */
  public componentRef!: ComponentRef<any>;

  /** The size of the modal */
  private size: ModalSize = 'base';

  /** CSS class list applied to the modal */
  public modalClass: string[] = [];

  /** Reactive signal for modal-specific alerts */
  protected readonly alertService: AlertService = inject(AlertService);
  protected modalAlerts: Signal<Alert[]> = this.alertService.getAlertsByLocation('modal');

  constructor(
    private readonly modalService: ModalService,
    private readonly confirmDialog: ConfirmDialogService,
  ) { }

  /**
   * Initializes the modal with configuration and styles.
   */
  public ngOnInit(): void {
    this.size = this.modalConfig.size ?? this.size;
    this.defineStyle();
    this.open = true;
  }

  /**
   * Injects the component content into the modal after the view is initialized,
   * then moves focus to the first text field inside the modal body.
   */
  public ngAfterViewInit(): void {
    this.injectModalContent();
    this.focusFirstField();
  }

  /**
   * After the injected component has rendered, moves focus to the first
   * text-like field (input / select / textarea) inside the modal body.
   *
   * Uses requestAnimationFrame so Angular's change-detection cycle for the
   * newly injected component completes before we query the DOM.
   *
   * Respects any element that already has focus (e.g. an explicit `autofocus`
   * on an `appInput`) — if something inside the modal wrapper is already
   * focused we leave it alone.
   */
  private focusFirstField(): void {
    requestAnimationFrame(() => {
      const wrapper = this.modalWrapper?.nativeElement as HTMLElement | undefined;
      if (!wrapper) return;

      const FIELD_SELECTOR =
        'input:not([type=checkbox]):not([type=radio]):not([type=hidden]):not([disabled]):not([readonly]),' +
        'select:not([disabled]),' +
        'textarea:not([disabled])';

      // Only defer to the current focus if it is already a form field inside
      // the modal (e.g. an explicit `autofocus` on an appInput). Buttons, links,
      // and the CDK-placed focus on the close button don't count — we override those.
      const active = document.activeElement as HTMLElement | null;
      if (active && wrapper.contains(active) && active.matches(FIELD_SELECTOR)) return;

      wrapper.querySelector<HTMLElement>(FIELD_SELECTOR)?.focus();
    });
  }

  /**
   * Dynamically injects a component into the modal container.
   */
  private injectModalContent(): void {
    if (this.modalConfig?.component && this.modalContainer) {
      this.modalContainer.clear();
      this.componentRef = injectComponent(
        this.modalContainer,
        this.modalConfig.component,
        this.modalConfig.params
      );
    }
  }

  /**
   * Updates the modal size dynamically and recalculates styles.
   *
   * @param size - New modal size
   */
  public updateSize(size: ModalSize): void {
    this.size = size;
    this.modalConfig.size = size;
    this.defineStyle();
  }

  /**
   * Resolves modal size to corresponding CSS width classes.
   * @param size - The desired modal size
   * @returns CSS class array
   */
  private defineSize(size: ModalSize): string[] {
    switch (size) {
      case 'xs': return ['w-1/6'];
      case 'sm': return ['w-1/3'];
      case 'base': return ['w-2/5'];
      case 'md': return ['w-1/2'];
      case 'lg': return ['w-3/5'];
      case 'xl': return ['w-4/5'];
    }
  }

  /**
   * Defines the overall modal style and visual layout based on config type and size.
   * @returns CSS class array
   */
  private defineStyle(): string[] {
    if (this.modalConfig) {
      if (this.modalConfig.type === 'middle' || this.modalConfig.type === 'center') {
        this.modalClass = [
          'fixed', 'flex', 'flex-col', 'justify-between', 'bg-white', 'dark:bg-dark-card', 'rounded-md',
          'left-1/2', 'top-1/2', 'transform', '-translate-x-1/2', '-translate-y-1/2',
          'shadow-sm', 'animate-fadein', 'text-primary', 'dark:text-dark-text', 'pointer-events-auto',
          'mh-32', 'h-auto', 'overflow-hidden', ...this.defineSize(this.size)
        ];
      } else if (this.modalConfig.type === 'side' || this.modalConfig.type === 'right' || !this.modalConfig.type) {
        this.modalClass = [
          'bg-white', 'dark:bg-dark-card', 'dark:text-dark-text', 'pointer-events-auto', 'flex', 'flex-col', 'order-l',
          'min-w-96', 'border-l-gray-medium', 'dark:border-l-dark-border', 'fixed', 'top-0', 'bottom-0',
          'right-0', 'z-20', 'animate-fadeinright',
          ...(this.size === 'base' ? ['w-1/4'] : this.defineSize(this.size))
        ];
      }
    }
    return [];
  }

  public animateOut(): void {
    if (!this.modalConfig.type || this.modalConfig.type === 'side' || this.modalConfig.type === 'right') {
      this.modalWrapper.nativeElement.classList.add('animate-fadeoutright');
    }
  }

  /**
   * Triggers the modal close animation and service dismissal.
   */
  public close(): void {
    this.animateOut();
    this.modalService.close();
  }

  public cancel(): void {
    const instance = this.componentRef?.instance as Partial<HasUnsavedChanges>;
    if (typeof instance?.hasUnsavedChanges === 'function' && instance.hasUnsavedChanges()) {
      this.confirmDialog.confirm({
        title: 'unsavedChanges.title',
        message: 'unsavedChanges.message',
        confirmLabel: 'unsavedChanges.leave',
        cancelLabel: 'unsavedChanges.stay',
        confirmVariant: 'danger',
      }).subscribe(confirmed => {
        if (confirmed) {
          this.animateOut();
          this.modalService.cancel();
        }
      });
      return;
    }

    this.animateOut();
    this.modalService.cancel();
  }

  /**
   * Handles the execution of a modal action, including callback or internal method,
   * and optionally closes the modal after.
   * @param action - The action configuration
   */
  public handleAction(action: ModalAction): void {
    if (action.callback && action.method) {
      console.error('MODAL ERROR', 'Attempting to employ both callback and method in same call.');
      return;
    }

    if (action.callback) {
      action.callback();
    }

    if (action.method && this.componentRef.instance[action.method]) {
      this.componentRef.instance[action.method]();
    }

    if (action.closeOnClick) {
      this.cancel();
    }
  }
}
